import React from 'react';

// Renders the `trace` array on a critical alert doc.
//
// Each entry has shape: { t: <ms relative to alert tMs (signed)>, e: "<type>", s: "<tag>"?, ...extras }
// `t` is typically negative (events that led up to the alert) and zero/positive
// for events from the alert itself.

const EVENT_STYLES = [
  { match: (e) => /error|_fail|failed|timeout/.test(e), classes: 'text-red-600 font-medium' },
  { match: (e) => e === 'alert.fired',                  classes: 'text-red-700 font-bold' },
  { match: (e) => e.startsWith('zoom.') || e.startsWith('zoom_'),     classes: 'text-fuchsia-600' },
  { match: (e) => e.startsWith('rknn.'),                              classes: 'text-cyan-700 font-medium' },
  { match: (e) => e.startsWith('camera.'),                            classes: 'text-green-700' },
  { match: (e) => e.startsWith('network.'),                           classes: 'text-amber-600 font-medium' },
  { match: (e) => e.startsWith('rtdb.'),                              classes: 'text-blue-600' },
  { match: (e) => e.startsWith('igloo.'),                             classes: 'text-indigo-600' },
  { match: (e) => e.startsWith('hand.'),                              classes: 'text-emerald-600' },
  { match: (e) => e.startsWith('boot.') || e.startsWith('signin.'),   classes: 'text-slate-700' },
  { match: (e) => e.startsWith('audio') || e.startsWith('hdmi'),      classes: 'text-amber-700' },
];

function eventClass(name) {
  if (!name) return 'text-foreground';
  for (const rule of EVENT_STYLES) {
    if (rule.match(name)) return rule.classes;
  }
  return 'text-foreground';
}

function formatRelT(t) {
  if (typeof t !== 'number') return '?';
  const sign = t < 0 ? '-' : '+';
  const s = Math.abs(t) / 1000;
  return `T${sign}${s.toFixed(2)}s`;
}

function formatExtraValue(v) {
  if (v === null) return 'null';
  if (typeof v === 'object') {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
}

function AlertTraceView({ trace, truncated }) {
  if (!trace || trace.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-6 text-center">
        No trace recorded for this alert.
      </div>
    );
  }

  const oldest = Math.min(...trace.map((e) => Number(e.t || 0)));
  const newest = Math.max(...trace.map((e) => Number(e.t || 0)));
  const windowS = ((newest - oldest) / 1000).toFixed(1);

  return (
    <div>
      <div className="text-xs text-muted-foreground mb-2">
        {trace.length} events · {windowS}s window
        {truncated && (
          <span className="ml-2 text-amber-600 font-medium">
            (truncated to fit size cap — oldest events dropped)
          </span>
        )}
      </div>
      <div className="space-y-0.5 font-mono text-xs">
        {trace.map((d, i) => {
          const extras = Object.entries(d).filter(([k]) => !['t', 'e', 's'].includes(k));
          return (
            <div key={i} className="flex items-baseline gap-3">
              <span className="text-muted-foreground tabular-nums w-20 text-right shrink-0">
                {formatRelT(Number(d.t))}
              </span>
              <span className={`${eventClass(d.e)} shrink-0 w-56 truncate`} title={d.e}>
                {d.e}
              </span>
              {d.s && (
                <span className="text-muted-foreground shrink-0" title={d.s}>
                  [{d.s}]
                </span>
              )}
              {extras.length > 0 && (
                <span className="text-muted-foreground truncate">
                  {extras.map(([k, v]) => `${k}=${formatExtraValue(v)}`).join('  ')}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default AlertTraceView;
